import mergeWith from 'lodash/mergeWith';
import { format } from 'date-fns';
import type {
	ContextualFields,
	IssueToCreate,
} from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { convertFieldToIssueCreateInput } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import type { JiraIssueCreateInput } from '../__generated__/CreateIssueControllerMutation.graphql';

const mapContextualFieldDataToInput = (
	payload: ContextualFields,
): Partial<JiraIssueCreateInput> => {
	let rank;
	if (payload.rankBeforeIssueId) {
		rank = {
			beforeIssueId: payload.rankBeforeIssueId,
		};
	} else if (payload.rankAfterIssueId) {
		rank = {
			afterIssueId: payload.rankAfterIssueId,
		};
	}

	return {
		rank,
	};
};

const generateRankInput = (payload: ContextualFields) => {
	if (payload.rankBeforeIssueId) {
		return {
			beforeIssueId: payload.rankBeforeIssueId,
		};
	}
	if (payload.rankAfterIssueId) {
		return {
			afterIssueId: payload.rankAfterIssueId,
		};
	}

	return undefined;
};

export const toIssueCreateInput = (
	{ project, issueType, summary, assignee, dueDate }: IssueToCreate,
	contextualFields: ContextualFields,
	groupContextFieldInput?: GroupContextField,
	isIssueRankingEnabled?: boolean,
): JiraIssueCreateInput | null => {
	if (!summary.trim().length) {
		return null;
	}

	const input: JiraIssueCreateInput = {
		projectId: project.id,
		issueTypeId: issueType.id,
		fields: {
			singleLineTextFields: [
				{
					fieldId: 'summary',
					text: summary,
				},
			],
			...(contextualFields.parentIssueAri !== undefined && {
				parentField: { issueId: contextualFields.parentIssueAri },
			}),
			...(groupContextFieldInput && convertFieldToIssueCreateInput(groupContextFieldInput)),
			...(assignee &&
				fg('jira_render_pic_in_nin') && {
					singleSelectUserPickerFields: [
						{ fieldId: 'assignee', user: { accountId: assignee.accountId } },
					],
				}),
			...(dueDate &&
				fg('jira_render_pic_in_nin') && {
					datePickerFields: [
						{ fieldId: 'duedate', date: { formattedDate: format(dueDate, 'dd/MMM/yy') } },
					],
				}),
		},
		rank:
			isIssueRankingEnabled && fg('jsc_list_reranking_gate')
				? generateRankInput(contextualFields)
				: undefined,
	};

	if (fg('jsc_list_reranking_gate')) {
		return input;
	}
	return mergeWith(input, mapContextualFieldDataToInput(contextualFields), (objValue, srcValue) => {
		if (Array.isArray(objValue)) {
			return objValue.concat(srcValue);
		}
	});
};
